import toast from '@liquidweb-dev/undercurrent/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LWApiPublicAccountUserStateDataListForKeyResultRawI } from '@liquidweb-dev/club-sauce/public/account/user/stateData/index.raw';
import type {
	LWApiPublicServerUpdateParamsRawI,
	LWApiPublicServerUpdateResultRawI,
} from '@liquidweb-dev/club-sauce/public/server/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { useUpdateFavorite } from 'modules/queries/account/user/stateData/useUpdate';
import api from 'modules/queries/api';
import { ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY } from 'modules/queries/account/user/stateData/useListForKey';
import useInvalidateAssets from 'modules/queries/invalidateQueries/useInvalidateAssets';
import useSnackbarError from 'utility/hooks/useSnackbarError';

const useServerUpdate = () => {
	const snackbarError = useSnackbarError();
	const invalidateAssets = useInvalidateAssets();

	return useMutation<
		LWApiPublicServerUpdateResultRawI,
		ApiErrorI,
		LWApiPublicServerUpdateParamsRawI
	>({
		mutationFn: (params) => api.server.apiRaw.update(params),
		onSuccess: (_data, params) => {
			invalidateAssets(params.uniq_id);
		},
		onError: snackbarError,
	});
};

/**
 * Updates the hostname (domain) of a server and attempts to update the "favorite" name saved to user/stateData/listforkey
 */
export const useUpdateHostname = () => {
	const queryClient = useQueryClient();

	const { mutate: updateServer, ...rest } = useServerUpdate();
	const { mutate: updateFavorite } = useUpdateFavorite();

	return {
		mutate: (
			{ domain, uniqId }: { domain: string; uniqId: string },
			options?: { onSuccess?: () => void },
		) =>
			updateServer(
				{ domain, uniq_id: uniqId },
				{
					onSuccess: () => {
						options?.onSuccess?.();
						toast.success('Hostname updated successfully.');

						const favoritesData =
							queryClient.getQueryData<LWApiPublicAccountUserStateDataListForKeyResultRawI>(
								[
									ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
									{ key: 'favorites' },
								],
							);

						// update the saved favorite name if it exists
						const favorite = favoritesData?.data.find(
							(fav) => fav.path === `/servers/${uniqId}`,
						);

						if (favorite) {
							updateFavorite({
								uuid: favorite.uuid,
								path: favorite.path as string,
								displayName: domain,
							});
						}
					},
				},
			),
		...rest,
	};
};

export default useServerUpdate;
